import { Capacitor } from "@capacitor/core";

const STORAGE_KEY = "screen0.premiumExplicitlyUnlocked";
const UNLOCK_CHANGED_EVENT = "screen0:premium-unlock-changed";

function notifyPremiumUnlockChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(UNLOCK_CHANGED_EVENT));
}

async function readNativeFlag(): Promise<boolean> {
  const { Preferences } = await import("@capacitor/preferences");
  const { value } = await Preferences.get({ key: STORAGE_KEY });
  return value === "1";
}

async function writeNativeFlag(): Promise<void> {
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.set({ key: STORAGE_KEY, value: "1" });
}

async function readEphemeralFlag(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      return await readNativeFlag();
    } catch (error) {
      console.warn("[premium] Preferences read failed, falling back to localStorage", error);
    }
  }
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

async function writeEphemeralFlag(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      await writeNativeFlag();
      return;
    } catch (error) {
      console.warn("[premium] Preferences write failed, falling back to localStorage", error);
    }
  }
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // Storage may be unavailable in some WebViews.
  }
}

/**
 * Install-scoped flag set only after a successful purchase or user-initiated
 * restore. RevenueCat may report active entitlements via getCustomerInfo on a
 * fresh iOS install (Apple ID sync) before the user taps Restore — gating must
 * ignore that until this flag is set.
 */
export async function readPremiumExplicitlyUnlocked(): Promise<boolean> {
  return readEphemeralFlag();
}

export async function markPremiumExplicitlyUnlocked(): Promise<void> {
  await writeEphemeralFlag();
  notifyPremiumUnlockChanged();
}

export function subscribePremiumUnlockChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(UNLOCK_CHANGED_EVENT, listener);
  return () => window.removeEventListener(UNLOCK_CHANGED_EVENT, listener);
}
